import { useEffect, useRef } from "react";
import { usePayments } from "../../../hooks/usePayments";
import { Heading, Layout } from "@vwfs-bronson/bronson-react";
import { useTranslation } from "react-i18next";
import { AdyenCheckout, Dropin, Card } from "@adyen/adyen-web";
import "@adyen/adyen-web/styles/adyen.css";
import {
  trackOnAdyenWidgetLoaded,
  trackOnAdyenWidgetError,
} from "../../../adobeTracking/trackers";

const DROPIN_CONTAINER_ID = "adyen-dropin-container";

export const AdyenDropIn = () => {
  const { t } = useTranslation("adyen");
  const {
    transaction,
    OverViewDetails,
    handleOnAdyenSuccess,
    handleOnAdyenError,
  } = usePayments();
  const dropinRef = useRef<HTMLDivElement>(null);
  const dropinInstanceRef = useRef<Dropin | null>(null);

  useEffect(() => {
    if (!transaction?.sessionId || !transaction?.sessionData) {
      return;
    }

    let mounted = true;

    const amountInMinorUnits = Math.round(
      (OverViewDetails?.outstandingBalance ?? 0) * 100,
    );

    const initializeDropIn = async () => {
      try {
        const checkout = await AdyenCheckout({
          environment:
            (process.env.REACT_APP_ADYEN_ENVIRONMENT as "test" | "live") ??
            "test",
          clientKey: process.env.REACT_APP_ADYEN_CLIENT_KEY ?? "",
          locale: "fr-FR",
          countryCode: "FR",
          amount: {
            value: amountInMinorUnits,
            currency: "EUR",
          },
          translations: {
            "fr-FR": {
              "creditCard.cardNumber.label": "Numéro de carte bancaire",
              "creditCard.securityCode.label": "Cryptogramme visuel",
              "creditCard.securityCode.contextualText.3digits":
                "Il s'agit des trois chiffres au dos de votre carte bancaire",
              "creditCard.expiryDate.contextualText": "Format MM/AA",
              "creditCard.holderName": "Titulaire de la carte (Prénom Nom ou Raison sociale)",
            },
          },
          session: {
            id: transaction.sessionId as string,
            sessionData: transaction.sessionData as string,
          },
          onPaymentCompleted: (result: any) => {
            handleOnAdyenSuccess(result);
          },
          onPaymentFailed: (result: any) => {
            handleOnAdyenError(result?.resultCode ?? "Payment failed");
          },
          onError: (error: any) => {
            trackOnAdyenWidgetError();
            handleOnAdyenError(error?.message ?? "Unknown Adyen error");
          },
        });

        if (!mounted) return;

        dropinInstanceRef.current = new Dropin(checkout, {
          paymentMethodComponents: [Card],
          paymentMethodsConfiguration: {
            card: {
              name: "Carte bancaire",
              hasHolderName: true,
              holderNameRequired: true,
              // Full-size 3DS2 challenge iframe (fills container) instead of default 390x400
              challengeWindowSize: "05",
            },
          },
        }).mount(`#${DROPIN_CONTAINER_ID}`) as Dropin;

        trackOnAdyenWidgetLoaded();

        if (dropinRef.current) {
          dropinRef.current.scrollIntoView({ behavior: "smooth" });
        }
      } catch (error) {
        trackOnAdyenWidgetError();
        handleOnAdyenError(
          error instanceof Error
            ? error.message
            : "Failed to initialize Adyen payment widget",
        );
      }
    };

    initializeDropIn();

    return () => {
      mounted = false;
      if (
        dropinInstanceRef.current &&
        typeof dropinInstanceRef.current.unmount === "function"
      ) {
        dropinInstanceRef.current.unmount();
        dropinInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.sessionId, transaction?.sessionData]);

  return (
    <Layout.Item default="2/3" s="1/1" className="u-mt-large">
      <div ref={dropinRef}>
        <Heading level={1} className="u-text-center">
          {t("header")}
        </Heading>
        <div className="u-text-center u-mb-small">
          <div>{t("subheader")}</div>
        </div>
        <div id={DROPIN_CONTAINER_ID}></div>
      </div>
    </Layout.Item>
  );
};

export default AdyenDropIn;
